// frameScale — frame downscaling for the timelapse replay
// (growth-timelapse-share-2026-07-02.md §5.1 「都度キャンバス合成を縮小
// (出力 720p 級) してエンコーダへ逐次供給 (フレームを溜め込まない)」).
//
// The replay driver composites the full canvas (`compositeAllForExport`,
// up to 4000² RGBA = 64 MB) once per frame and immediately shrinks it to
// the output size before yielding — the original-resolution buffer never
// accumulates. Two scaler implementations behind one function type:
//
//   - `createCanvasFrameScaler()` — OffscreenCanvas `drawImage` with
//     high-quality smoothing. The production path (browser main thread
//     AND workers both have OffscreenCanvas); hardware-accelerated, ~1 ms
//     per frame.
//   - `boxDownscaleRgba` — pure-TS area-average fallback. Deterministic
//     and DOM-free, used by unit / integration tests (vitest node env)
//     and as a safety net where OffscreenCanvas is unavailable.
//
// `createFrameScaler()` picks the best available at runtime.

export interface FrameSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Straight-α RGBA bytes over a plain `ArrayBuffer` (same convention as
 * drawing-core's `PixelBuffer` — the `ImageData` ctor rejects
 * SharedArrayBuffer-backed views, so the narrowing is load-bearing).
 */
export type FrameRgba = Uint8ClampedArray<ArrayBuffer>;

/**
 * Output dimensions for a `maxEdge` cap:
 *   - never upscale (scale is clamped to 1),
 *   - both edges rounded DOWN to even numbers — H.264 4:2:0 (the T2
 *     WebCodecs target; X only accepts H.264 mp4) requires even
 *     dimensions, and handling it here means T2 never has to crop,
 *   - floor of 2 px per edge so degenerate inputs stay encodable.
 */
export const computeOutputSize = (srcW: number, srcH: number, maxEdge: number): FrameSize => {
  if (srcW <= 0 || srcH <= 0 || maxEdge <= 0) {
    throw new TypeError(`computeOutputSize: invalid dims ${srcW}x${srcH} maxEdge ${maxEdge}`);
  }
  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
  const even = (v: number): number => Math.max(2, Math.floor((v * scale) / 2) * 2);
  return { width: even(srcW), height: even(srcH) };
};

/**
 * Scale a straight-α RGBA buffer (`sw × sh`) down to `dw × dh`. May
 * return the input buffer itself when no resample is needed — callers
 * that retain frames across iterations must copy (the T2 encoder consumes
 * each frame before the next is produced, so the hot path doesn't).
 */
export type FrameScaler = (
  src: FrameRgba,
  sw: number,
  sh: number,
  dw: number,
  dh: number,
) => FrameRgba;

/**
 * Pure-TS area-average (box filter) downscale. Exact coverage weighting
 * for non-integer ratios; identity fast-path when dims match. O(src
 * pixels) per frame — fine for tests and small canvases, but the
 * OffscreenCanvas scaler should carry production (see module header).
 */
export const boxDownscaleRgba: FrameScaler = (src, sw, sh, dw, dh) => {
  if (src.length !== sw * sh * 4) {
    throw new TypeError(`boxDownscaleRgba: src length ${src.length} != ${sw}x${sh}x4`);
  }
  if (dw === sw && dh === sh) return src;
  if (dw > sw || dh > sh) {
    throw new TypeError(`boxDownscaleRgba: upscale not supported (${sw}x${sh} -> ${dw}x${dh})`);
  }
  const out = new Uint8ClampedArray(dw * dh * 4);
  const xRatio = sw / dw;
  const yRatio = sh / dh;
  for (let dy = 0; dy < dh; dy++) {
    const sy0 = dy * yRatio;
    const sy1 = (dy + 1) * yRatio;
    for (let dx = 0; dx < dw; dx++) {
      const sx0 = dx * xRatio;
      const sx1 = (dx + 1) * xRatio;
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      let weight = 0;
      for (let sy = Math.floor(sy0); sy < sy1 && sy < sh; sy++) {
        // Vertical overlap of source row `sy` with the box [sy0, sy1).
        const wy = Math.min(sy + 1, sy1) - Math.max(sy, sy0);
        if (wy <= 0) continue;
        for (let sx = Math.floor(sx0); sx < sx1 && sx < sw; sx++) {
          const wx = Math.min(sx + 1, sx1) - Math.max(sx, sx0);
          if (wx <= 0) continue;
          const w = wx * wy;
          const i = (sy * sw + sx) * 4;
          r += (src[i] ?? 0) * w;
          g += (src[i + 1] ?? 0) * w;
          b += (src[i + 2] ?? 0) * w;
          a += (src[i + 3] ?? 0) * w;
          weight += w;
        }
      }
      const o = (dy * dw + dx) * 4;
      if (weight > 0) {
        out[o] = Math.round(r / weight);
        out[o + 1] = Math.round(g / weight);
        out[o + 2] = Math.round(b / weight);
        out[o + 3] = Math.round(a / weight);
      }
    }
  }
  return out;
};

/**
 * OffscreenCanvas-backed scaler (production). Returns `null` when the
 * runtime has no OffscreenCanvas (vitest node env / very old browsers).
 *
 * Canvases are cached across calls and only reallocated on a dimension
 * change, so the per-frame cost is one `putImageData` + one smoothed
 * `drawImage` + one `getImageData`.
 */
export const createCanvasFrameScaler = (): FrameScaler | null => {
  if (typeof OffscreenCanvas === 'undefined') return null;
  let srcCanvas: OffscreenCanvas | null = null;
  let srcCtx: OffscreenCanvasRenderingContext2D | null = null;
  let dstCanvas: OffscreenCanvas | null = null;
  let dstCtx: OffscreenCanvasRenderingContext2D | null = null;

  return (src, sw, sh, dw, dh) => {
    if (dw === sw && dh === sh) return src;
    if (srcCanvas === null || srcCanvas.width !== sw || srcCanvas.height !== sh) {
      srcCanvas = new OffscreenCanvas(sw, sh);
      srcCtx = srcCanvas.getContext('2d');
    }
    if (dstCanvas === null || dstCanvas.width !== dw || dstCanvas.height !== dh) {
      dstCanvas = new OffscreenCanvas(dw, dh);
      dstCtx = dstCanvas.getContext('2d');
    }
    if (srcCtx === null || dstCtx === null) {
      // Context acquisition failed (GPU process trouble) — degrade to the
      // pure path rather than aborting the whole timelapse.
      return boxDownscaleRgba(src, sw, sh, dw, dh);
    }
    srcCtx.putImageData(new ImageData(src, sw, sh), 0, 0);
    dstCtx.imageSmoothingEnabled = true;
    dstCtx.imageSmoothingQuality = 'high';
    dstCtx.clearRect(0, 0, dw, dh);
    dstCtx.drawImage(srcCanvas, 0, 0, sw, sh, 0, 0, dw, dh);
    return new Uint8ClampedArray(dstCtx.getImageData(0, 0, dw, dh).data);
  };
};

/** Best available scaler: OffscreenCanvas in browsers/workers, box in node. */
export const createFrameScaler = (): FrameScaler => createCanvasFrameScaler() ?? boxDownscaleRgba;
